/**
 * studioMotion.js — the Email Studio motion library (3-tier system).
 * Personality: playful-but-alive. Studio-only — sent emails stay static.
 *
 * TOKENS: durations 150–400ms, ease-out / spring. Idle loops (orb breathe,
 * dot pulse) are ambient states, not transitions — slower by design.
 * RULES: transform/opacity only (GPU), fires once, never blocks streaming.
 * KILL-SWITCHES: prefers-reduced-motion AND html[data-motion="off"] both
 * collapse everything to near-instant — centrally, in CSS, no JS branches.
 */
import { prefersReduced } from './motion';

export const DUR = { fast: 150, base: 220, slow: 300, moment: 400 };
export const EASE = {
  out: 'cubic-bezier(0.22, 1, 0.36, 1)',
  spring: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
  glide: 'cubic-bezier(0.4, 0, 0.2, 1)',
};

const KEY = 'km-motion';
export const motionEnabled = () => {
  try { return localStorage.getItem(KEY) !== 'off'; } catch { return true; }
};
export const setMotionEnabled = (on) => {
  try { localStorage.setItem(KEY, on ? 'on' : 'off'); } catch { /* private mode */ }
  document.documentElement.dataset.motion = on ? 'on' : 'off';
};
export const reduced = () => prefersReduced() || document.documentElement.dataset.motion === 'off';

/** One-shot class runner for Tier-3 moments (star flourish, shimmer, etc.). */
export const runOnce = (el, cls, ms = 600) => {
  if (!el || !el.isConnected || reduced()) return;
  el.classList.remove(cls);
  void el.offsetWidth; // restart the animation
  el.classList.add(cls);
  setTimeout(() => { if (el.isConnected) el.classList.remove(cls); }, ms);
};

const KM_CSS = `
:root{--km-fast:${DUR.fast}ms;--km-base:${DUR.base}ms;--km-slow:${DUR.slow}ms;--km-moment:${DUR.moment}ms;--km-out:${EASE.out};--km-spring:${EASE.spring};}

/* ── TIER 1 — MICRO ──────────────────────────────────────────────────── */
.km-studio button{transition:transform var(--km-fast) var(--km-out),opacity var(--km-fast) linear;}
.km-studio button:active:not(:disabled){transform:scale(0.96);}
.km-studio input:focus,.km-studio textarea:focus,.km-studio select:focus{box-shadow:0 0 0 3px rgba(124,92,246,0.28),0 0 18px rgba(124,92,246,0.16);}
.km-studio input,.km-studio textarea,.km-studio select{transition:box-shadow var(--km-base) var(--km-out),border-color var(--km-base) var(--km-out);}
.km-studio [data-testid^="studio-mode-"],.km-studio [data-testid^="studio-group-"]{transition:color var(--km-base) var(--km-out),background-color var(--km-base) var(--km-out),border-color var(--km-base) var(--km-out),transform var(--km-fast) var(--km-out);}
.km-chip{transition:transform var(--km-base) var(--km-out),box-shadow var(--km-base) var(--km-out);will-change:transform;}
.km-chip:hover{transform:translateY(-2px);box-shadow:0 8px 20px rgba(0,0,0,0.35),0 0 0 1px rgba(124,92,246,0.55),-6px 0 16px -6px rgba(0,229,195,0.35),6px 0 16px -6px rgba(255,46,154,0.3);}
.km-chip:active{transform:translateY(0) scale(0.97);}
@keyframes kmRiseIn{from{opacity:0;transform:translateY(8px);}to{opacity:1;transform:none;}}
.km-stagger>*{animation:kmRiseIn 150ms var(--km-out) both;}
.km-stagger>*:nth-child(1){animation-delay:0ms}.km-stagger>*:nth-child(2){animation-delay:14ms}.km-stagger>*:nth-child(3){animation-delay:28ms}.km-stagger>*:nth-child(4){animation-delay:42ms}.km-stagger>*:nth-child(5){animation-delay:56ms}.km-stagger>*:nth-child(6){animation-delay:70ms}.km-stagger>*:nth-child(7){animation-delay:84ms}.km-stagger>*:nth-child(8){animation-delay:98ms}.km-stagger>*:nth-child(9){animation-delay:112ms}.km-stagger>*:nth-child(n+10){animation-delay:120ms}
/* Neon dots: calm by default — only "Replied" carries a slow, subtle glow. */
@keyframes kmReplyGlow{0%,100%{opacity:1;}50%{opacity:0.78;}}
.km-dot{animation:none;}
.km-dot[data-kstate="replied"]{animation:kmReplyGlow 3.2s ease-in-out infinite;}

/* ── TIER 2 — FLOW (AI writing) ──────────────────────────────────────── */
@keyframes kmBlockIn{from{opacity:0;transform:translateY(10px) scale(0.99);}to{opacity:1;transform:none;}}
.km-block-in{animation:kmBlockIn 200ms var(--km-out) both;}
@keyframes kmStageIn{from{opacity:0;transform:translateY(6px);}to{opacity:1;transform:none;}}
.km-stage-in{display:inline-block;animation:kmStageIn var(--km-base) var(--km-out) both;}
@keyframes kmBreathe{0%,100%{transform:scale(1);box-shadow:0 0 16px rgba(196,72,240,0.5);}50%{transform:scale(1.05);box-shadow:0 0 22px rgba(196,72,240,0.7),0 0 32px rgba(0,229,195,0.2);}}
.km-orb-think{animation:kmBreathe 2.4s ease-in-out infinite;}
@keyframes kmSettle{from{transform:scale(1.12);}to{transform:scale(1);}}
.km-orb-settle{animation:kmSettle var(--km-slow) var(--km-spring) 1;}
@keyframes kmSuggestPulse{0%,100%{box-shadow:0 0 22px rgba(124,92,246,0.45);}50%{box-shadow:0 0 28px rgba(124,92,246,0.75),0 0 38px rgba(0,229,195,0.25);}}
.km-suggest{animation:kmSuggestPulse 1.4s ease-in-out 2;}
@keyframes kmChatIn{from{opacity:0;transform:translateY(8px);}to{opacity:1;transform:none;}}
.km-chat-in{animation:kmChatIn var(--km-base) var(--km-out) both;}

/* ── TIER 3 — MOMENT (rare, earned) ──────────────────────────────────── */
.km-sweep-bar{position:absolute;inset:0;overflow:hidden;border-radius:inherit;pointer-events:none;}
@keyframes kmSweep{from{transform:translateX(-100%);}to{transform:translateX(100%);}}
.km-sweep-bar::after{content:'';position:absolute;inset:0;background:linear-gradient(95deg,transparent 0%,rgba(0,229,195,0.28) 25%,rgba(124,92,246,0.34) 50%,rgba(255,46,154,0.28) 75%,transparent 100%);animation:kmSweep 550ms var(--km-out) 1 both;}
@keyframes kmSentPop{0%{transform:scale(1);}45%{transform:scale(1.08);}100%{transform:scale(1);}}
.km-sent-ok{animation:kmSentPop var(--km-moment) var(--km-spring) 1;}
@keyframes kmStar{0%{transform:scale(1) rotate(0deg);}35%{transform:scale(1.6) rotate(72deg);}70%{transform:scale(0.9) rotate(144deg);}100%{transform:scale(1) rotate(144deg);}}
.km-star-run{animation:kmStar 500ms var(--km-spring) 1;}
.km-shimmer{position:relative;overflow:hidden;}
@keyframes kmShimmer{from{transform:translateX(-120%) skewX(-12deg);}to{transform:translateX(260%) skewX(-12deg);}}
.km-shimmer-run::after{content:'';position:absolute;top:0;bottom:0;left:0;width:45%;background:linear-gradient(100deg,transparent,rgba(255,255,255,0.35),transparent);animation:kmShimmer 600ms var(--km-out) 1 both;pointer-events:none;}
@keyframes kmBloom{0%{box-shadow:0 0 0 0 rgba(196,72,240,0.85);transform:scale(1);}40%{transform:scale(1.8);}100%{box-shadow:0 0 0 14px rgba(196,72,240,0);transform:scale(1);}}
.km-bloom{animation:kmBloom 700ms var(--km-out) 1;}
@keyframes kmBusyPulse{0%,100%{opacity:1;}50%{opacity:0.55;}}
.km-busy-pulse{animation:kmBusyPulse 1.1s ease-in-out infinite;}

/* ── KILL-SWITCHES — reduced motion + central toggle ─────────────────── */
@media (prefers-reduced-motion: reduce){
  .km-studio *,.km-studio,.km-dot,.km-bloom,.km-chip,.km-stagger>*,.km-block-in,.km-chat-in,.km-stage-in,.km-orb-think,.km-orb-settle,.km-suggest,.km-sent-ok,.km-star-run,.km-busy-pulse,.km-sweep-bar::after,.km-shimmer-run::after{animation-duration:0.01ms !important;animation-iteration-count:1 !important;transition-duration:0.01ms !important;}
  .km-chip:hover{transform:none;}
}
html[data-motion="off"] .km-studio *,html[data-motion="off"] .km-studio,html[data-motion="off"] .km-dot,html[data-motion="off"] .km-bloom,html[data-motion="off"] .km-chip,html[data-motion="off"] .km-stagger>*,html[data-motion="off"] .km-block-in,html[data-motion="off"] .km-chat-in,html[data-motion="off"] .km-stage-in,html[data-motion="off"] .km-orb-think,html[data-motion="off"] .km-orb-settle,html[data-motion="off"] .km-suggest,html[data-motion="off"] .km-sent-ok,html[data-motion="off"] .km-star-run,html[data-motion="off"] .km-busy-pulse,html[data-motion="off"] .km-sweep-bar::after,html[data-motion="off"] .km-shimmer-run::after{animation-duration:0.01ms !important;animation-iteration-count:1 !important;transition-duration:0.01ms !important;}
html[data-motion="off"] .km-chip:hover{transform:none;}
`;

/** Inject the studio motion stylesheet once (idempotent, app-wide safe). */
export const ensureMotionStyles = () => {
  if (typeof document === 'undefined' || document.getElementById('km-motion-css')) return;
  const s = document.createElement('style');
  s.id = 'km-motion-css';
  s.textContent = KM_CSS;
  document.head.appendChild(s);
  document.documentElement.dataset.motion = motionEnabled() ? 'on' : 'off';
};
